import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { ChangeEvent, SyntheticEvent, useEffect, useState } from "react";

interface CheckoutFormProps {
  amount: number;
  processingFees: number;
  coverFees: boolean;
  isUpdatingAmount?: boolean;
  onCoverFeesChecked: (e: ChangeEvent<HTMLInputElement>) => void;
}

export default function CheckoutForm({
  amount,
  processingFees,
  coverFees,
  isUpdatingAmount = false,
  onCoverFeesChecked,
}: CheckoutFormProps) {
  const stripe = useStripe();
  const elements = useElements();

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [message, setMessage] = useState<
    undefined | { message: string; error: boolean }
  >({ message: "", error: false });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!stripe) return;

    const clientSecret = new URLSearchParams(window.location.search).get(
      "payment_intent_client_secret"
    );
    if (!clientSecret) return;

    stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
      switch (paymentIntent?.status) {
        case "succeeded":
          setMessage({ message: "Payment succeeded!", error: false });
          break;
        case "processing":
          setMessage({ message: "Your payment is processing...", error: false });
          break;
        case "requires_payment_method":
          setMessage({
            message: "Your payment was not successful, please try again.",
            error: true,
          });
          break;
        default:
          setMessage({
            message: "Something went wrong, please try again.",
            error: true,
          });
          break;
      }
    });
  }, [stripe]);

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setIsLoading(true);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Make sure to change this to your payment completion page
        return_url: `${
          process.env.NODE_ENV === "development"
            ? "http://localhost:1313"
            : `${window.location.origin}`
        }/donate`,
        receipt_email: email,
        payment_method_data: {
          billing_details: {
            name,
            phone,
            address: {
              line1: address,
              city,
            },
          },
        },
      },
    });

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.
    if (error.type === "card_error" || error.type === "validation_error") {
      setMessage({ message: error.message as string, error: true });
    } else {
      setMessage({ message: "An unexpected error occurred.", error: true });
    }

    setIsLoading(false);
  };

  return (
    <form
      id="payment-form"
      onSubmit={handleSubmit}
      className="w-full p-4 md:max-w-[500px] mx-auto"
    >
      {stripe && elements && (
        <>
          <label
            htmlFor="email"
            style={{
              fontFamily:
                '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif',
              color: "#30313d",
              fontSize: "0.93rem",
              fontWeight: "400",
              marginBottom: "0.25rem",
              transition:
                "transform 0.5s cubic-bezier(0.19, 1, 0.22, 1), opacity 0.5s cubic-bezier(0.19, 1, 0.22, 1)",
            }}
          >
            Email
          </label>
          <input
            id="email"
            type="text"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="rounded-md mb-4 p-3 bg-white"
            style={{
              border: "1px solid rgba(50, 50, 93, 0.1)",
              maxHeight: 44,
              width: "100%",
              boxShadow:
                "0px 1px 1px rgb(0 0 0 / 3%), 0px 3px 6px rgb(0 0 0 / 2%)",
            }}
          />

          <label
            htmlFor="name"
            style={{
              fontFamily:
                '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif',
              color: "#30313d",
              fontSize: "0.93rem",
              fontWeight: "400",
              marginBottom: "0.25rem",
              transition:
                "transform 0.5s cubic-bezier(0.19, 1, 0.22, 1), opacity 0.5s cubic-bezier(0.19, 1, 0.22, 1)",
            }}
          >
            Full name
          </label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="rounded-md mb-4 p-3 bg-white"
            style={{
              border: "1px solid rgba(50, 50, 93, 0.1)",
              maxHeight: 44,
              width: "100%",
              boxShadow:
                "0px 1px 1px rgb(0 0 0 / 3%), 0px 3px 6px rgb(0 0 0 / 2%)",
            }}
          />
          <label
            htmlFor="address"
            style={{
              fontFamily:
                '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif',
              color: "#30313d",
              fontSize: "0.93rem",
              fontWeight: "400",
              marginBottom: "0.25rem",
              transition:
                "transform 0.5s cubic-bezier(0.19, 1, 0.22, 1), opacity 0.5s cubic-bezier(0.19, 1, 0.22, 1)",
            }}
          >
            Address
          </label>
          <input
            id="address"
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            className="rounded-md mb-4 p-3 bg-white"
            style={{
              border: "1px solid rgba(50, 50, 93, 0.1)",
              maxHeight: 44,
              width: "100%",
              boxShadow:
                "0px 1px 1px rgb(0 0 0 / 3%), 0px 3px 6px rgb(0 0 0 / 2%)",
            }}
          />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="city"
                style={{
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif',
                  color: "#30313d",
                  fontSize: "0.93rem",
                  fontWeight: "400",
                  marginBottom: "0.25rem",
                  transition:
                    "transform 0.5s cubic-bezier(0.19, 1, 0.22, 1), opacity 0.5s cubic-bezier(0.19, 1, 0.22, 1)",
                }}
              >
                City
              </label>
              <input
                id="city"
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="rounded-md mb-4 p-3 bg-white"
                style={{
                  border: "1px solid rgba(50, 50, 93, 0.1)",
                  maxHeight: 44,
                  width: "100%",
                  boxShadow:
                    "0px 1px 1px rgb(0 0 0 / 3%), 0px 3px 6px rgb(0 0 0 / 2%)",
                }}
              />
            </div>
            <div>
              <label
                htmlFor="phone"
                style={{
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif',
                  color: "#30313d",
                  fontSize: "0.93rem",
                  fontWeight: "400",
                  marginBottom: "0.25rem",
                  transition:
                    "transform 0.5s cubic-bezier(0.19, 1, 0.22, 1), opacity 0.5s cubic-bezier(0.19, 1, 0.22, 1)",
                }}
              >
                Phone number
              </label>
              <input
                id="phone"
                type="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="rounded-md mb-4 p-3 bg-white"
                style={{
                  border: "1px solid rgba(50, 50, 93, 0.1)",
                  maxHeight: 44,
                  width: "100%",
                  boxShadow:
                    "0px 1px 1px rgb(0 0 0 / 3%), 0px 3px 6px rgb(0 0 0 / 2%)",
                }}
              />
            </div>
          </div>
        </>
      )}
      <PaymentElement id="payment-element" className="mb-4" />
      {/* <label className="label cursor-pointer justify-start">
        <input
          type="checkbox"
          className="checkbox checkbox-accent checkbox-sm"
          checked={coverFees}
          onChange={onCoverFeesChecked}
        />
        <span className="label-text ml-2">
          {`I choose to add $${(processingFees / 100).toFixed(
            2
          )} to cover the fees to process my donation.`}
        </span>
      </label> */}
      <div className="flex items-center mb-4">
        <input
          id="default-checkbox"
          type="checkbox"
          checked={coverFees}
          onChange={onCoverFeesChecked}
          disabled={isUpdatingAmount}
          className="w-4 h-4 text-blue-600 bg-gray-100 rounded border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
        />
        <label
          htmlFor="default-checkbox"
          className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
        >
          {`I choose to add $${(processingFees / 100).toFixed(
            2
          )} to cover the fees to process my donation.`}
        </label>
      </div>
      <button
        type="submit"
        disabled={isLoading || !stripe || !elements || isUpdatingAmount}
        id="submit"
        className="bg-primary hover:bg-primary-focus text-white font-bold cursor-pointer px-4 py-3 border-0 rounded shadow-md w-full disabled:opacity-50"
      >
        <span>
          {isLoading
            ? "Processing..."
            : isUpdatingAmount
            ? "Updating amount..."
            : `Donate $${(amount / 100).toFixed(2)}`}
        </span>
      </button>
      {message?.message && (
        <p
          className={`flex justify-center mt-4 py-2 font-bold ${
            message.error
              ? "bg-error text-red-900"
              : "bg-success text-green-900"
          }`}
        >
          {message.message}
        </p>
      )}
    </form>
  );
}
